<template>
  <div class="main_Container">
    <section class="page-head">
      <div class="container-fluid">
        <h1 class="page-head__title m-0">الرئيسية</h1>
      </div>
    </section>

    <section class="analysis">
      <div class="container-fluid">
        <!-- Key counts. One tile per metric, driven by `stats`. -->
        <div class="stat-grid">
          <article
            v-for="stat in stats"
            :key="stat.key"
            class="stat-card"
            :class="`stat-card--${stat.accent}`"
            :style="{ '--stat-icon': `url(${stat.icon})` }"
          >
            <span class="stat-card__icon" aria-hidden="true"></span>
            <div class="stat-card__body">
              <h2 class="stat-card__label">{{ stat.label }}</h2>
              <p class="stat-card__value">{{ stat.value }}</p>
            </div>
          </article>
        </div>

        <!-- Ranked shortlists. Each panel carries its own empty state so the
             section keeps its shape before the store resolves. -->
        <div class="panel-grid">
          <section v-for="list in topLists" :key="list.key" class="panel">
            <header class="panel__head">
              <h2 class="panel__title">{{ list.title }}</h2>
              <span class="panel__count">{{ list.items.length }}</span>
            </header>
            <ol v-if="list.items.length" class="rank-list">
              <li
                v-for="(item, index) in list.items"
                :key="index"
                class="rank-list__row"
              >
                <span class="rank-list__rank">{{ index + 1 }}</span>
                <span class="rank-list__title">{{ item.title }}</span>
              </li>
            </ol>
            <p v-else class="panel__empty">لا توجد بيانات بعد</p>
          </section>
        </div>

        <div class="report">
          <div class="card">
            <div class="card-header">
              <h5 class="card-header-title">تقرير موجز شهري</h5>
              <div class="card-tools">
                <button
                  class="btn"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#monthlyReport"
                  aria-expanded="true"
                  aria-controls="monthlyReport"
                >
                  <i class="fas fa-minus"></i>
                  <i class="fas fa-plus"></i>
                </button>
              </div>
            </div>

            <div class="card-body collapse show" id="monthlyReport">
              <div class="row g-4">
                <div class="col-lg-4">
                  <p class="report__subtitle">استكمال الهدف</p>
                  <div
                    v-for="goal in goals"
                    :key="goal.label"
                    class="progress-group"
                  >
                    <div class="progress__header">
                      <span class="progress__title">{{ goal.label }}</span>
                      <span class="progress__value">
                        <b>{{ goal.current }}</b
                        >/{{ goal.target }}
                      </span>
                    </div>
                    <div class="progress">
                      <div
                        class="progress-bar"
                        role="progressbar"
                        :style="{
                          width: percent(goal) + '%',
                          backgroundColor: goal.color,
                        }"
                        :aria-valuenow="goal.current"
                        aria-valuemin="0"
                        :aria-valuemax="goal.target"
                      ></div>
                    </div>
                  </div>
                </div>
                <div class="col-lg-8">
                  <ChartLine />
                </div>
              </div>
            </div>

            <div class="card-footer">
              <div class="summary">
                <div
                  v-for="item in summary"
                  :key="item.label"
                  class="summary__item"
                >
                  <span class="summary__delta" :class="`is-${item.trend}`">
                    <i :class="trendIcon(item.trend)"></i> {{ item.delta }}
                  </span>
                  <h5 class="summary__value">{{ item.value }}</h5>
                  <span class="summary__label">{{ item.label }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ChartLine from "@/chart/ChartLine";

export default {
  data() {
    return {
      analytics: {},
      topPayments: [],
      topViews: {},
      recentlyAdded: {},
      // Placeholder figures — not wired to the store yet.
      goals: [
        { label: "الطلاب", current: 160, target: 200, color: "var(--brand)" },
        { label: "الاختبارات", current: 310, target: 400, color: "var(--danger)" },
        { label: "الاكواد", current: 480, target: 800, color: "var(--success)" },
        { label: "الدروس", current: 250, target: 500, color: "var(--info)" },
        { label: "الفصول", current: 250, target: 500, color: "var(--warning)" },
      ],
      // Placeholder figures — not wired to the store yet.
      summary: [
        { label: "إجمالي الإيرادات", value: "$24,813.53", delta: "20%", trend: "up" },
        { label: "التكلفة الإجمالية", value: "$10,390.90", delta: "0%", trend: "flat" },
        { label: "اجمالي الربح", value: "$24,813.53", delta: "20%", trend: "up" },
        { label: "الأهداف المكتملة", value: "1200", delta: "18%", trend: "down" },
      ],
    };
  },
  components: {
    ChartLine,
  },
  computed: {
    ...mapGetters("analyticsModule", [
      "isFirstLoad",
      "getAnalytics",
      "getTopPayments",
      "getTopViews",
      "getRecentlyAdded",
    ]),
    stats() {
      const counts = this.analytics || {};
      return [
        { key: "lessons", label: "الدروس", icon: require("@/assets/login-icon/learn.svg"), accent: "brand", value: this.nFormatter(counts.lessons) },
        { key: "codes", label: "الاكواد", icon: require("@/assets/login-icon/code.svg"), accent: "info", value: this.nFormatter(counts.codes) },
        { key: "classes", label: "الفصول", icon: require("@/assets/login-icon/school-solid.svg"), accent: "success", value: this.nFormatter(counts.classes) },
        { key: "exams", label: "الاختبارات", icon: require("@/assets/login-icon/tests.svg"), accent: "warning", value: this.nFormatter(counts.exams) },
        { key: "infographics", label: "الصورة التوضيحية", icon: require("@/assets/login-icon/images-solid.svg"), accent: "brand", value: this.nFormatter(counts.infographics) },
        { key: "students", label: "الطلاب", icon: require("@/assets/login-icon/users-solid.svg"), accent: "info", value: this.nFormatter(counts.students) },
        { key: "files", label: "الملفات", icon: require("@/assets/login-icon/file-solid.svg"), accent: "success", value: this.nFormatter(counts.files) },
        { key: "videos", label: "الفيديوهات", icon: require("@/assets/login-icon/video-solid.svg"), accent: "warning", value: this.nFormatter(counts.videos) },
        { key: "views", label: "المشاهدات", icon: require("@/assets/login-icon/eye-solid.svg"), accent: "brand", value: this.nFormatter(counts.views) },
      ];
    },
    topLists() {
      return [
        { key: "payments", title: "الاكثر مبيعاً", items: this.asArray(this.topPayments) },
        { key: "views", title: "الاكثر مشاهدة", items: this.asArray(this.topViews) },
        { key: "recent", title: "المضاف حديثاً", items: this.asArray(this.recentlyAdded) },
      ];
    },
  },
  methods: {
    ...mapActions("analyticsModule", [
      "fetchTopPayments",
      "fetchTopViews",
      "fetchRecentlyAdded",
    ]),
    // The store hands these back as either an array or a keyed object.
    asArray(value) {
      if (Array.isArray(value)) return value;
      return Object.values(value || {});
    },
    percent(goal) {
      if (!goal.target) return 0;
      return Math.min(100, Math.round((goal.current / goal.target) * 100));
    },
    trendIcon(trend) {
      if (trend === "up") return "fas fa-caret-up";
      if (trend === "down") return "fas fa-caret-down";
      return "fas fa-caret-left";
    },
    nFormatter(num, digits) {
      const lookup = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "G" },
        { value: 1e12, symbol: "T" },
        { value: 1e15, symbol: "P" },
        { value: 1e18, symbol: "E" },
      ];
      const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
      var item = lookup
        .slice()
        .reverse()
        .find(function (item) {
          return num >= item.value;
        });
      return item
        ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol
        : "0";
    },
  },
  async created() {
    this.analytics = this.getAnalytics;
    // Analytics Functions
    await this.fetchTopPayments();
    await this.fetchTopViews();
    await this.fetchRecentlyAdded();
    //-------------------------------
  },
  mounted() {
    this.analytics = this.getAnalytics;
    this.topPayments = this.getTopPayments;
    this.topViews = this.getTopViews;
    this.recentlyAdded = this.getRecentlyAdded;
  },
  watch: {
    getAnalytics() {
      this.analytics = this.getAnalytics;
    },
    getTopPayments() {
      this.topPayments = this.getTopPayments;
    },
    getTopViews() {
      this.topViews = this.getTopViews;
    },
    getRecentlyAdded() {
      this.recentlyAdded = this.getRecentlyAdded;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/abstracts/_mixins.scss";
@import "@/scss/abstracts/_variables.scss";

/*========== Stat tiles ==========*/
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: var(--space-4);
}

.stat-card {
  // Accent is swapped by the modifier classes below; everything else is shared.
  --stat-accent: var(--brand);
  --stat-accent-bg: var(--brand-100);

  @include surface(var(--radius-lg), var(--shadow-xs));
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4);
  transition: box-shadow var(--transition), transform var(--transition),
    border-color var(--transition);

  &:hover {
    box-shadow: var(--shadow);
    border-color: var(--border);
    transform: translateY(-2px);
  }

  &__icon {
    position: relative;
    flex-shrink: 0;
    width: 46px;
    height: 46px;
    border-radius: var(--radius);
    background-color: var(--stat-accent-bg);

    // Masking the SVG lets the glyph take a token colour, so the tile stays
    // correct in dark mode instead of needing an invert filter.
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      margin: auto;
      width: 22px;
      height: 22px;
      background-color: var(--stat-accent);
      -webkit-mask: var(--stat-icon) center / contain no-repeat;
      mask: var(--stat-icon) center / contain no-repeat;
    }
  }

  &__body {
    min-width: 0;
  }

  &__label {
    margin: 0 0 2px;
    font-size: var(--small-font-size);
    font-weight: var(--font-medium);
    color: var(--text-secondary);
    @include nowarp;
  }

  &__value {
    margin: 0;
    font-size: var(--h1-font-size);
    font-weight: var(--font-bold);
    line-height: 1.1;
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  &--info {
    --stat-accent: var(--info);
    --stat-accent-bg: var(--info-bg);
  }

  &--success {
    --stat-accent: var(--success);
    --stat-accent-bg: var(--success-bg);
  }

  &--warning {
    --stat-accent: var(--warning);
    --stat-accent-bg: var(--warning-bg);
  }
}

/*========== Ranked shortlists ==========*/
.panel-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--space-4);
  margin-top: var(--space-6);
}

.panel {
  @include surface(var(--radius-lg), var(--shadow-xs));
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &__head {
    @include flex($direction: row, $justify: space-between, $align: center);
    gap: var(--space-3);
    padding: var(--space-4) var(--space-5);
    border-bottom: 1px solid var(--border-subtle);
  }

  &__title {
    margin: 0;
    font-size: var(--h3-font-size);
    font-weight: var(--font-semi-bold);
    color: var(--text-primary);
  }

  &__count {
    flex-shrink: 0;
    padding: 2px 10px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background-color: var(--bg-surface-sunken);
    color: var(--text-secondary);
    font-size: var(--smaller-font-size);
    font-weight: var(--font-semi-bold);
    font-variant-numeric: tabular-nums;
  }

  &__empty {
    margin: 0;
    padding: var(--space-7) var(--space-5);
    text-align: center;
    color: var(--text-muted);
    font-size: var(--small-font-size);
  }
}

.rank-list {
  list-style: none;
  margin: 0;
  padding: 0;

  &__row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-5);
    transition: background-color var(--transition-fast);

    &:not(:last-child) {
      border-bottom: 1px solid var(--border-subtle);
    }

    &:hover {
      background-color: var(--bg-hover);
    }

    // Podium places read differently from the rest of the list.
    &:nth-child(-n + 3) .rank-list__rank {
      background-color: var(--brand-100);
      border-color: transparent;
      color: var(--brand);
    }
  }

  &__rank {
    @include flex($direction: row, $justify: center, $align: center);
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background-color: var(--bg-surface-sunken);
    color: var(--text-secondary);
    font-size: var(--smaller-font-size);
    font-weight: var(--font-semi-bold);
    font-variant-numeric: tabular-nums;
  }

  &__title {
    color: var(--text-primary);
    font-size: var(--small-font-size);
    @include nowarp;
  }
}

/*========== Monthly report ==========*/
.report {
  margin-top: var(--space-6);
  padding-bottom: var(--space-6);

  .card-header {
    @include flex($direction: row, $justify: space-between, $align: center);
  }

  .card-tools button {
    position: relative;
    width: 30px;
    height: 30px;
    box-shadow: none;
    color: var(--text-secondary);

    svg {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &.collapsed svg.fa-minus {
      opacity: 0;
    }

    &:not(.collapsed) svg.fa-plus {
      opacity: 0;
    }
  }

  .card-body {
    padding: var(--space-5);
  }

  &__subtitle {
    margin-bottom: var(--space-4);
    text-align: center;
    font-weight: var(--font-semi-bold);
    color: var(--text-primary);
  }
}

.progress-group {
  margin-bottom: var(--space-4);
}

.progress__header {
  @include flex($direction: row, $justify: space-between, $align: center);
  margin-bottom: var(--space-2);
  font-size: var(--small-font-size);
}

.progress__title {
  color: var(--text-secondary);
}

.progress__value {
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
}

.progress {
  height: 8px;
  border-radius: var(--radius-pill);
  background-color: var(--bg-surface-sunken);
  overflow: hidden;
}

.progress-bar {
  border-radius: var(--radius-pill);
  transition: width var(--transition-slow);
}

.summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--space-4);

  &__item {
    padding: var(--space-4);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius);
    background-color: var(--bg-surface);
    text-align: center;
  }

  &__delta {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: var(--smaller-font-size);
    font-weight: var(--font-semi-bold);

    &.is-up {
      color: var(--success);
    }

    &.is-down {
      color: var(--danger);
    }

    &.is-flat {
      color: var(--warning);
    }
  }

  &__value {
    margin: var(--space-1) 0 2px;
    font-size: var(--h2-font-size);
    font-weight: var(--font-bold);
    color: var(--text-primary);
    font-variant-numeric: tabular-nums;
  }

  &__label {
    font-size: var(--small-font-size);
    color: var(--text-secondary);
  }
}
</style>
